import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Icon, type IconName } from '../../../components/ui/Icon';
import { useAuthStore } from '../../../stores/auth.store';
import { ADMIN_PAGES, canSeeAdminPage, type AdminPageKey } from '../../../lib/adminRoutes';
import { ActionStream } from '../../oggi/ActionStream';
import { api } from '../../../lib/api';
import { orarioEsatto, situazioneQuery, type StatoSync } from '../situazione/situazioneApi';
import '../situazione/situazione.css';

/**
 * I pezzi della Situazione che passano nel Calendario dei pagamenti
 * (16/10/2026: Situazione e Fisco escono dal menu, il calendario diventa la
 * prima pagina dell'Amministrazione). Stessi componenti e stessa API della
 * Situazione: qui si montano e basta.
 */

/** Le cose da fare oggi dell'Amministrazione (lo stesso flusso della pagina Oggi). */
export function CoseDaFare({ compatta }: { compatta?: boolean }) {
  // 26/10/2026: niente titolo "Cose da fare oggi" sopra: lo dicono già i gruppi della lista
  // ("Cose da fare subito", "Cose da fare oggi"), prima compariva due volte.
  return (
    <section className="ed-section sit-block cal-cose">
      <div className="card">
        <div className="card-bd card-bd--tight"><ActionStream scope="admin" max={7} compact={compatta} /></div>
      </div>
    </section>
  );
}

/** Quello che serve all'avviso, da GET /admin/sync/status. */
interface StatoCollegamenti {
  qonto: { connected: boolean };
  /** I collegamenti fermi davvero, gia' in parole (lib/allarmi-sync.ts lato API). */
  allarmi?: Array<{ collegamento: string; testo: string; cosaFare: string; impostazioni: boolean; errore: string | null }>;
}

const statoCollegamentiQuery = {
  queryKey: ['admin', 'sync-status'] as const,
  queryFn: () => api.get<StatoCollegamenti>('/admin/sync/status'),
  // Stesso ritmo della Situazione. Dopo "Aggiorna banca e fatture" la rilegge l'invalidate di ['admin'].
  staleTime: 60_000,
  refetchInterval: 120_000,
  refetchOnWindowFocus: true,
};

/**
 * In cima al calendario, una riga per guasto, con il rimedio: Qonto non
 * collegato (senza, saldi e movimenti non arrivano) e i collegamenti fermi
 * davvero (Qonto, FattureInCloud, Stripe e il backup notturno del database).
 * "Fermo davvero" lo decide l'API (lib/allarmi-sync.ts): non il
 * tentativo andato male una volta, ma un guasto che dura, lo stesso che fa
 * partire la campanella e la mail. La riga sparisce da sola quando un
 * aggiornamento torna a riuscire. Prima leggeva la Situazione intera per un
 * solo sì o no: ora legge lo stato dei collegamenti, che costa molto meno.
 */
export function AvvisoCollegamenti() {
  const navigate = useNavigate();
  const { data: s } = useQuery(statoCollegamentiQuery);
  if (!s) return null;
  const servizi = () => navigate('/settings?voce=integrazioni');
  return (
    <>
      {!s.qonto.connected && (
        <div className="adm-note text-warn">
          <Icon name="alert" size={12} />
          <span>Qonto non è collegato: senza, i saldi e i movimenti del conto non arrivano. <a className="underline cursor-pointer" onClick={servizi}>Collegalo dalle Impostazioni</a>.</span>
        </div>
      )}
      {/* Qonto e FattureInCloud fermi stanno già fra le cose da fare qui sotto, col
          tasto Riprova: ripeterli in cima diceva due volte la stessa cosa (27/10/2026).
          Qui resta quello che le cose da fare non dicono: Stripe e il backup notturno. */}
      {(s.allarmi ?? []).filter((a) => a.collegamento !== 'qonto.sync' && a.collegamento !== 'fic.sync').map((a) => (
        <div key={a.collegamento} className="adm-note text-warn" title={a.errore ? `Ultimo errore: ${a.errore}` : undefined}>
          <Icon name="alert" size={12} />
          <span>
            {a.testo}{' '}
            {a.impostazioni
              ? <><a className="underline cursor-pointer" onClick={servizi}>{a.cosaFare.replace(/\.$/, '')}</a>.</>
              : a.cosaFare}
          </span>
        </div>
      ))}
    </>
  );
}

/** Quando sono arrivati gli ultimi dati da Qonto e da FattureInCloud. */
export function DaDoveVengono() {
  const { data: s } = useQuery(situazioneQuery({ mesi: 1 }));
  if (!s) return null;
  return (
    <section className="ed-section sit-block">
      <div className="data-hd"><span className="t-micro">Da dove vengono i numeri</span></div>
      <div className="sit-fonti">
        <Fonte icon="landmark" nome="Banca (Qonto)" stato={s.sync.qonto} />
        <Fonte icon="invoice" nome="Fatture (FattureInCloud)" stato={s.sync.fic} />
        <div className="sit-fonte">
          <Icon name="percent" size={12} className="mute" />
          <span><strong>Tasse</strong>: le cifre segnate «stima» sono calcoli nostri e cambiano quando arrivano gli F24 del commercialista; cartelle e avvisi vengono dai documenti arrivati in PEC.</span>
        </div>
      </div>
    </section>
  );
}

function Fonte({ icon, nome, stato }: { icon: IconName; nome: string; stato: StatoSync }) {
  const errore = stato.errore ?? stato.erroreUltime24h;
  return (
    <div className={`sit-fonte${stato.problema ? ' text-alert' : ''}`} title={errore ? `Ultimo errore: ${errore}` : orarioEsatto(stato.ultimoOk)}>
      <Icon name={icon} size={12} className={stato.problema ? '' : 'mute'} />
      <span><strong>{nome}</strong>: {stato.testo}.</span>
    </div>
  );
}

/**
 * Telefono: senza sidebar, le altre pagine dell'Amministrazione si raggiungono
 * da qui (prima lo faceva la Situazione, che era la prima pagina).
 */
export function AltrePagine({ qui }: { qui: AdminPageKey }) {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const pagine = ADMIN_PAGES.filter((p) => !p.nascosta && p.key !== qui && canSeeAdminPage(user, p));
  return (
    <div className="ed-section adm-altre">
      <div className="data-hd"><span className="t-micro">Le altre pagine dell'Amministrazione</span></div>
      {pagine.map((p) => (
        <div
          key={p.key}
          className="adm-line cursor-pointer"
          role="link"
          tabIndex={0}
          onClick={() => navigate(p.to)}
          onKeyDown={(e) => { if (e.key === 'Enter') navigate(p.to); }}
        >
          <div className="icon-tile sm"><Icon name={p.icon} size={16} /></div>
          <div className="flex-1 min-w-0">
            <div className="font-semibold">{p.label}</div>
            <div className="mute text-[12px]">{p.sub}</div>
          </div>
          <Icon name="chevronR" size={12} className="mute" />
        </div>
      ))}
    </div>
  );
}
